import { DestroyRef, Injectable, WritableSignal, inject, signal } from '@angular/core';
import { Location, ViewportScroller } from '@angular/common';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { Observable, Subject, catchError, map, of, shareReplay, switchMap, take, tap, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { Globals } from '@core/globals';
import { GtmService } from '@services/gtm.service';
import { FilterDataService } from '@services/filter-data.service';
import { ListService } from '@services/list.service';
import { FilterTagsService } from '@services/filter-tags.service';
import { ListUrlService } from '@services/list-url.service';
import { FilterParams, FilterParamsActiveMakesModels, FilterParamsMap, FilterPredefined, FilterTags } from '@models/filter.model';
import { SortDropdownItems } from '@models/sort-dropdown.model';
import { FilterDataMessage } from '@models/filter-data.model';
import { ListBanner, ListDataResponse } from '@models/list.model';

@Injectable({
  providedIn: 'root',
})
export class FilterService {
  private router = inject(Router);
  private location = inject(Location);
  private activatedRoute = inject(ActivatedRoute);
  private viewportScroller = inject(ViewportScroller);
  private destroyRef = inject(DestroyRef);
  private translateService = inject(TranslateService);
  private globals = inject(Globals);
  private filterTagsService = inject(FilterTagsService);
  private filterDataService = inject(FilterDataService);
  private listUrlService = inject(ListUrlService);
  private gtmService = inject(GtmService);
  private listService = inject(ListService);
  public page: WritableSignal<number> = signal(1);
  public sort: WritableSignal<number> = signal(1);
  public filterParams: WritableSignal<FilterParams> = signal({});
  public filterParams$ = toObservable(this.filterParams);
  public filterParamsMap: FilterParamsMap = {
    priceFrom: { key: 'priceFrom', id: 'id', title: 'title' },
    priceTo: { key: 'priceTo', id: 'id', title: 'title' },
    yearFrom: { key: 'yearFrom', id: 'id', title: 'id' },
    yearTo: { key: 'yearTo', id: 'id', title: 'id' },
    mileageFrom: { key: 'mileageFrom', id: 'id', title: 'title' },
    mileageTo: { key: 'mileageTo', id: 'id', title: 'title' },
    performanceFrom: { key: 'performanceFrom', id: 'id', title: 'title' },
    performanceTo: { key: 'performanceTo', id: 'id', title: 'title' },
    engineCapacityId: { key: 'engineCapacity', id: 'id', title: 'title' },
    bodyTypeId: { key: 'bodyType', id: 'id', title: 'title' },
    driveTypeId: { key: 'drive', id: 'id', title: 'title' },
    fuelTypeId: { key: 'fuelType', id: 'id', title: 'name' },
    gearboxId: { key: 'gearbox', id: 'id', title: 'name' },
    seatsNoId: { key: 'seats', id: 'id', title: 'title' },
    doorsNo: { key: 'doors', id: 'id', title: 'title' },
    externalColor: { key: 'color', id: 'id', title: 'name' },
    isVATDeduct: { key: 'isVATDeduct', id: 'isVATDeduct', title: '' },
    is4x4: { key: 'is4x4', id: 'is4x4', title: '' },
    isNew: { key: 'isNew', id: 'isNew', title: '' },
    isDemo: { key: 'isDemo', id: 'isDemo', title: '' },
    equipment: { key: 'equipment', id: 'id', title: 'title' },
    daysInStockFrom: { key: 'daysInStockFrom', id: 'id', title: '' },
    daysInStockTo: { key: 'daysInStockTo', id: 'id', title: '' },
    withOldPrice: { key: 'withOldPrice', id: 'withOldPrice', title: '' },
    monthlyPaymentFrom: { key: 'monthlyPaymentFrom', id: 'id', title: 'title' },
    monthlyPaymentTo: { key: 'monthlyPaymentTo', id: 'id', title: 'title' },
    leasingAdvance: { key: 'leasingAdvance', id: 'id', title: 'title' },
    leasingNumberOfMonth: { key: 'leasingNumberOfMonth', id: 'id', title: 'title' },
    isAvailable: { key: 'isAvailable', id: 'isAvailable', title: '' },
  };
  public activeMakesModels: WritableSignal<FilterParamsActiveMakesModels[]> = signal([]);
  public activeTitle: WritableSignal<string | undefined> = signal(undefined);
  public predefinedPrices: WritableSignal<FilterPredefined[]> = signal([]);
  public predefinedYears: WritableSignal<FilterPredefined[]> = signal([]);
  public sortItems: WritableSignal<SortDropdownItems[]> = signal([]);
  public tags: WritableSignal<FilterTags[]> = signal([]);
  public canLoadCars: boolean = true;
  private lastBanner: number = 0;
  private activeMake: any;
  private bannerIndex: number = 0;
  public loadListDataSubject = new Subject<string>();
  public pageFrom: WritableSignal<number> = signal(1);
  public loadMoreDirection: WritableSignal<'prev' | 'next'> = signal('next');

  getFilter(): Observable<FilterDataMessage> {
    return this.filterDataService.getFilter().pipe(
      shareReplay(1),
      map((response) => {
        return response;
      }),
    );
  }

  removeTag(tag: FilterTags): void {
    if (tag.key === 'comingSoon') {
      this.setParams({ ...this.filterParams(), daysInStockFrom: undefined, daysInStockTo: undefined });
      return;
    }
    if (tag.key === 'inStock') {
      this.setParams({ ...this.filterParams(), daysInStockFrom: undefined });
      return;
    }
    let item = this.filterParams()[tag.key as keyof FilterParams];
    if (!item) return;
    if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
      item = undefined;
    } else if (tag.key === 'makeModelId') {
      for (let [index, make] of item.entries()) {
        const makeValue = make as any;
        if (tag.subKey === 'model') {
          for (let model of makeValue.modelId) {
            if (model === tag.value) {
              makeValue.modelId.splice(makeValue.modelId.indexOf(model), 1);
            }
          }
        } else {
          if (makeValue.makeId === tag.value) {
            item.splice(index, 1);
          }
        }
      }
    } else {
      item.forEach((value: any, index: number) => {
        if (Array.isArray(item) && value === tag.value) {
          item?.splice(index, 1);
        }
      });
      if (item.length === 0) item = undefined;
    }

    this.setParams({ ...this.filterParams(), [tag.key]: item });
  }

  checkUrlParam(filterParams: FilterParams, param: string, index: number): FilterParams | 'error' {
    if (!param) return filterParams;
    if (index === 1) {
      const data = this.filterDataService.filterData()?.make.find((item) => item.slug === param); // Check if first param is make
      if (data) {
        this.activeMake = data;
        filterParams['makeModelId'] = [{ makeId: data.id, modelId: [] }];
        return filterParams;
      }
    } else if (index === 2 && this.activeMake) {
      const data = this.activeMake.model.find((item: any) => item.slug === param); // Check if second param is model
      if (data) {
        filterParams['makeModelId'] = [{ makeId: this.activeMake.id, modelId: [data.id] }];
        return filterParams;
      }
    }
    if (index <= 3) {
      const data = this.filterDataService.filterData()?.bodyType.find((item) => item.slug === param); // Check if first - third param is body type
      if (data) {
        filterParams['bodyTypeId'] = [data.id];
        return filterParams;
      }
    }
    if (index <= 4) {
      const data = this.filterDataService.filterData()?.fuelType.find((item) => item.slug === param); // Check if first - fourth param is fuel type
      if (data) {
        filterParams['fuelTypeId'] = [data.id];
        return filterParams;
      }
    }
    if (index <= 5) {
      const data = this.filterDataService.filterData()?.gearbox.find((item) => item.slug === param); // Check if first - fifth param is gearbox
      if (data) {
        filterParams['gearboxId'] = [data.id];
        return filterParams;
      }
    }
    if (index <= 6) {
      const data = this.filterDataService.filterData()?.yearFrom.find((item) => item.slug.toString() === param); // Check if first - sixth param is year from
      if (data) {
        filterParams['yearFrom'] = data.id;
        return filterParams;
      }
    }
    return 'error'; // Not found
  }

  loadUrlParams(url: string): void {
    const urlFragments: string = url.split('#')?.[1];
    const hashParams: Params = {};
    this.sort.set(1);
    if (urlFragments) {
      const fragments: string[] = urlFragments.split('&');
      const containsSort = fragments.some(item => item.startsWith('sort='));
      if (!containsSort) this.sort.set(1);
      for (let fragment of fragments) {
        const param: string[] = fragment.split('=');
        if (param[0] === 'sort') {
          this.sort.set(Number(param[1]));
          continue;
        }
        hashParams[param[0]] = param[1];
      }
    }

    let urlFragments2 = url.split('#')?.[0];
    urlFragments2 = urlFragments2?.split('?')?.[1];
    const queryParams: Params = {};
    if (urlFragments2) {
      const fragments: string[] = urlFragments2.split('&');
      for (let fragment of fragments) {
        const param: string[] = fragment.split('=');
        queryParams[param[0]] = param[1];
      }
    }
    if (queryParams['page']) {
      this.pageFrom.set(Number(queryParams['page']));
    } else {
      this.pageFrom.set(1);
    }
    const page: number = parseInt(queryParams['page'], 10) || 1;
    this.page.set(page);

    let filterParams: FilterParams = {};
    const urlParams: string[] = url.split('?')[0].split('#')[0].split('/');
    const startIndex: number = 2;
    const listUrl: string = this.globals.routes()['list'].url;
    this.activeMake = undefined;
    for (const [index, value] of urlParams.entries()) {
      if (index < startIndex) continue;
      const paramIndex: number = index - startIndex + 1;
      const data = this.checkUrlParam(filterParams, value, paramIndex);
      if (data === 'error') {
        this.router.navigate([listUrl]);
        break;
      } else {
        filterParams = data;
      }
    }

    for (let key in hashParams) {
      const isArray: boolean = key.includes('-array');
      const property: string = isArray ? key.replace('-array', '') : key;
      let param: any;
      if (key === 'makes') {
        if (!Array.isArray(filterParams['makeModelId'])) filterParams['makeModelId'] = [];
        const makes: string[] = hashParams[key as keyof Params].split('-');
        for (let make of makes) {
          let found = false;
          filterParams['makeModelId']?.forEach((item: any) => {
            if (item.makeId === Number(make)) found = true;
          });
          if (!found) filterParams['makeModelId'].push({ makeId: Number(make), modelId: [] });
        }
      } else if (key.includes('models-')) {
        if (!Array.isArray(filterParams['makeModelId'])) filterParams['makeModelId'] = [];
        const makeId: number = Number(key.replace('models-', ''));
        let modelsId: number[] = [];
        const value = hashParams[key as keyof Params];
        if (value) {
          let models: string[] = value.split('-');
          for (let i = 0; i < models.length; i++) {
            modelsId.push(Number(models[i]));
          }
          let found = false;
          filterParams['makeModelId']?.forEach((item: any) => {
            if (item.makeId === makeId) {
              item.modelId = modelsId;
              found = true;
            }
          });
          if (!found) filterParams['makeModelId'].push({ makeId: makeId, modelId: modelsId });
        }
      } else {
        if (!this.filterParamsMap.hasOwnProperty(property)) continue;
        if (isArray) {
          const params: string[] = hashParams[key as keyof Params].split('-');
          param = params?.map((item) => {
            return this.convertParam(item);
          });
        } else {
          const value = hashParams[key as keyof Params];
          param = this.convertParam(value);
        }
        filterParams[property as keyof FilterParams] = param;
      }
    }
    if (filterParams !== this.filterParams()) this.filterParams.set(filterParams);
  }

  convertParam(value: string): number | boolean | string {
    if (/^\d+$/.test(value)) return Number(value);
    else if (value === 'true') return true;
    else if (value === 'false') return false;
    else return value;
  }

  setParams(data: FilterParams): void {
    this.page.set(1);

    this.getFilter()
      .pipe(take(1))
      .subscribe((filterData: FilterDataMessage) => {
        const url = this.listUrlService.generateUrl(data, filterData);
        url.fragment = url.fragment ? url.fragment + '&sort=' + this.sort() : 'sort=' + this.sort();
        const finalUrl: string = this.router.createUrlTree([url.url], { relativeTo: this.activatedRoute, fragment: url.fragment, queryParams: { page: this.page() > 1 ? this.page() : undefined } }).toString();
        this.location.go(finalUrl);
      });
  }

  filterParamsChanged(getList: boolean, loadCars?: boolean): Observable<ListDataResponse | undefined> {
    const tags = this.filterTagsService.generateTags(this.filterDataService.filterData(), this.filterParams(), this.filterParamsMap);
    this.tags.set(tags);

    if (getList) {
      let makes = [];
      let models = [];
      let fuels = [];
      let priceFrom;
      let priceTo;
      let yearFrom;
      let yearTo;
      let colors = [];
      for (let tag of this.tags()) {
        if (tag.key === 'makeModelId' && tag.subKey === undefined) makes.push(tag.title);
        else if (tag.key === 'makeModelId' && tag.subKey === 'model') models.push(tag.title);
        else if (tag.key === 'fuelTypeId') fuels.push(tag.title);
        else if (tag.key === 'priceFrom') priceFrom = tag.title ? 'from: ' + tag.title : '';
        else if (tag.key === 'priceTo') priceTo = tag.title ? 'to: ' + tag.title : '';
        else if (tag.key === 'color') colors.push(tag.title);
        else if (tag.key === 'yearFrom') yearFrom = tag.title ? 'from: ' + tag.title : '';
        else if (tag.key === 'yearTo') yearTo = tag.title ? 'to: ' + tag.title : '';
      }

      const make = makes.length ? makes.join(', ') : undefined;
      const model = models.length ? models.join(', ') : undefined;
      const fuel = fuels.length ? fuels.join(', ') : undefined;
      const price = priceFrom || priceTo ? (priceFrom ?? '') + (priceFrom && priceTo ? ' - ' : '') + (priceTo ?? '') : undefined;
      const year = yearFrom || yearTo ? (yearFrom ?? '') + (yearFrom && yearTo ? ' - ' : '') + (yearTo ?? '') : undefined;
      const color = colors.length ? colors.join(', ') : undefined;
      const dataLayer = {
        znacka: make,
        model: model,
        palivo: fuel,
        rokVyroby: year,
        cena: price,
        barva: color,
      };

      if (make || model || fuel || year || price || color) this.gtmService.pushGtmDataPage(dataLayer);

      if (loadCars !== false) {
        this.canLoadCars = true;
        return this.getList();
      }
    }
    return of(undefined);
  }

  loadPrev(): void {
    if (this.page() <= 1) return;
    this.pageFrom.update((value) => value - 1);
    this.loadMoreDirection.set('prev');
    this.getList(true, 'prev', this.pageFrom()).pipe(take(1)).subscribe();
    this.gtmService.pushGtmPageType({ event: 'click_vypis_predchozi_vozy' });
  }

  loadMore(): void {
    if (this.page() >= this.listService.pages()) return;
    this.page.update((value) => value + 1);
    this.loadMoreDirection.set('next');
    this.getList(true).pipe(take(1)).subscribe();
    this.gtmService.pushGtmPageType({ event: 'click_vypis_dalsi_vozy' });
  }

  setPage(page: number): void {
    this.page.set(page);
    this.viewportScroller.scrollToPosition([0, 0]);
  }

  changeSort(sort: number): void {
    this.sort.set(sort);
    this.page.set(1);
    this.setParams(this.filterParams());
  }

  insertBannersIntoCars(start: number): Observable<number> {
    return this.filterDataService.getBannersList().pipe(
      map((banners: ListBanner[] | undefined) => {
        const cars = this.listService.cars();
        if (start < 0) start = 0;
        let lastBanner = start;

        if (!banners || banners.length === 0) return lastBanner;

        for (let i = start + 18; i < cars.length; i += 36) {
          cars.splice(i, 0, banners[this.bannerIndex]);
          this.bannerIndex++;
          if (this.bannerIndex >= banners.length) this.bannerIndex = 0;
          lastBanner = i + 18;
        }

        return lastBanner;
      }),
    );
  }

  clearBanners(): void {
    this.lastBanner = 0;
    this.listService.cars.update((value) => value.filter((car) => car.hasOwnProperty('engine')));
  }

  getList(loadMore: boolean = false, type?: 'prev' | 'next', page?: number): Observable<ListDataResponse> {
    return this.listService.getListData({ data: this.filterParams(), page: page ?? this.page(), sort: this.sort(), loadMore }).pipe(
      tap((response: ListDataResponse) => {
        this.listService.setPages(response.message.total);
        if (loadMore) {
          if (type === 'prev') {
            this.listService.cars.set([...response.message.items, ...this.listService.cars()]);
          } else {
            this.listService.cars.set([...this.listService.cars(), ...response.message.items]);
          }
        } else {
          this.loadMoreDirection.set('next');
          this.listService.cars.set(response.message.items);
        }
        this.bannerIndex = 0;
        this.lastBanner = 0;
        this.clearBanners();
      }),
      switchMap((response: ListDataResponse) =>
        this.insertBannersIntoCars(this.lastBanner).pipe(
          tap((lastBanner: number) => {
            this.lastBanner = lastBanner;
          }),
          map(() => response),
        ),
      ),
      catchError((error) => {
        this.listService.setPages(0);
        this.listService.cars.set([]);
        this.listService.loaded.set(true);
        return throwError(() => new Error(error));
      }),
    );
  }

  valueToArray(value: any): any {
    if (Array.isArray(value)) return value;
    else return [value];
  }

  setMakeModelsNames(): void {
    this.getFilter()
      .pipe(take(1))
      .subscribe((data: any) => {
        const activeMakesModels: FilterParamsActiveMakesModels[] = [];
        this.filterParams().makeModelId?.forEach((makeModel) => {
          const make = data.make.find((make: any) => make.id === makeModel.makeId);
          if (!make) return;
          const activeMake: FilterParamsActiveMakesModels = { makeId: make.id, makeSlug: make.slug, makeTitle: make.title };
          activeMake.modelId = [];
          activeMake.modelSlug = [];
          activeMake.modelTitle = [];
          makeModel.modelId.forEach((modelId: number) => {
            const model = make.model.find((model: any) => model.id === modelId);
            if (!model) return;
            activeMake.modelId?.push(model.id);
            activeMake.modelSlug?.push(model.slug);
            activeMake.modelTitle?.push(model.title);
          });
          activeMakesModels.push(activeMake);
        });
        this.activeMakesModels?.set(activeMakesModels);
      });
  }

  setFuelGearboxBodyTypeNames(): void {
    this.getFilter()
      .pipe(take(1))
      .subscribe((data: any) => {
        let activeFuel: string | undefined = undefined;
        const fuel = this.filterParams().fuelTypeId?.map((item) => data.fuelType.find((fuel: any) => fuel.id === item));
        if (fuel && fuel.length === 1) {
          activeFuel = fuel[0].name?.toLowerCase();
        }

        let activeGearbox: string | undefined = undefined;
        const gearbox = this.filterParams().gearboxId?.map((item) => data.gearbox.find((gearbox: any) => gearbox.id === item));
        if (gearbox && gearbox.length === 1) {
          if (gearbox[0].id === 'A') {
            activeGearbox = this.translateService.instant('list.titleGearboxAutomatic');
          } else if (gearbox[0].id === 'M') {
            activeGearbox = this.translateService.instant('list.titleGearboxManual');
          }
        }

        let activeBodyType: string | undefined = undefined;
        const bodyType = this.filterParams().bodyTypeId?.map((item) => data.bodyType.find((bodyType: any) => bodyType.id === item));
        if (bodyType && bodyType.length === 1) activeBodyType = bodyType[0].title;

        let activeYear: number | undefined = this.filterParams().yearFrom;

        const finalTitle = [];
        if (activeFuel) finalTitle.push(activeFuel);
        if (activeGearbox) finalTitle.push(activeGearbox);
        if (activeBodyType) finalTitle.push(activeBodyType);
        if (activeYear) finalTitle.push(activeYear.toString());
        const makesModels = this.activeMakesModels();
        let makeModelTitle = '';
        if (makesModels.length === 1) {
          const item = makesModels[0];
          if (item) {
            makeModelTitle = item.makeTitle;
            if (item.modelTitle) {
              makeModelTitle += ' ';
              for (let i = 0; i < item.modelTitle.length; i++) {
                makeModelTitle += item.modelTitle[i] + (i === item.modelTitle.length - 1 ? '' : ', ');
              }
            }
          }
        }
        const startTitle = this.activeMakesModels().length > 0 && (activeFuel || (!activeGearbox && finalTitle.length > 1)) ? ', ' : ' ';
        this.activeTitle.set(makeModelTitle.trim() + startTitle + finalTitle.join(', '));
      });
  }

  setPredefined(): void {
    const year: number = new Date().getFullYear();
    const predefinedYears: FilterPredefined[] = [
      { value: year - 3, title: this.translateService.instant('filter.year.upTo', { years: '3' }) },
      { value: year - 6, title: this.translateService.instant('filter.year.upTo', { years: '6' }) },
      { value: year - 10, title: this.translateService.instant('filter.year.upTo', { years: '10' }) },
    ];
    this.predefinedYears.set(predefinedYears);
  }

  loadListData(url: string): Observable<ListDataResponse | undefined> {
    this.loadUrlParams(url);
    this.setMakeModelsNames();
    this.setFuelGearboxBodyTypeNames();
    return this.filterParamsChanged(this.canLoadCars);
  }

  constructor() {
    if (this.translateService.store.translations[this.translateService.currentLang]) {
      this.setPredefined();
    }
    this.translateService.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      setTimeout(() => {
        this.setPredefined();
        this.getFilter().subscribe();
      }, 1);
    });
    this.filterDataService.getBannersList().subscribe();

    this.loadListDataSubject
      .pipe(
        switchMap((data) => {
          return this.loadListData(data).pipe(
            catchError(() => {
              return [];
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
